import { useEffect, useRef, useState } from 'react'
import { useCampusSessionForm } from '../../../local'
import { setAcademicPeriod, setCampus, useDispatch, useSelector } from '@ggs/store'
import { Loader } from '../../loader'
import { useI18n } from '@ggs/i18n'
import './MenuCampusSessionForm.scss'
import { cn } from '@ggs/utils'

/**
 * @typedef {object} MenuItemProps
 * @property {string} label
 * @property {string} value
 * @property {MenuItemProps[]=} subMenu
 * @property {'campus'|'session'=} subMenuType
 * @property {boolean=} isActive
 */

/**
 * @typedef {object} SubMenuProps
 * @property {MenuItemProps[]=} subMenu
 * @property {'campus'|'session'=} subMenuType
 * @property {boolean=} isActive
 * @property {() => void=} onClose
 */

/**
 * Menu select UBC campus / session form.
 * @return {JSX.Element}
 */
export function MenuCampusSessionForm() {
  const campus = useSelector((state) => state.settings.campus)
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const dispatch = useDispatch()

  /**
   * Submit handler.
   * @param {Object<string, any>} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    setSubmitting(false)
  }

  const { FormInstance, loading } = useCampusSessionForm({
    onSubmitHandler,
    showFieldLabels: false,
  })

  /**
   * @type {MenuItemProps[]}
   */
  const campusItems = FormInstance.fields.campus.options

  useEffect(() => {
    if (FormInstance.context?.values) {
      const newCampus = FormInstance.context?.values?.campus || ''
      const newAcademicPeriod = FormInstance.context?.values?.session || ''

      if (newCampus && campus !== newCampus) {
        dispatch(setCampus(newCampus))
      }

      if (newAcademicPeriod && academicPeriod !== newAcademicPeriod) {
        dispatch(setAcademicPeriod(newAcademicPeriod))
      }
    }
  }, [FormInstance.context?.values])

  if (loading) {
    return <Loader />
  }

  /**
   *
   * @param {{ label: string, value: string }[]} items
   * @param {string} value
   * @param {string} defaultValue
   * @return {string}
   */
  const getValueLabel = (items, value, defaultValue) => {
    const item = items.find((item) => item.value === value)
    return item?.label || defaultValue
  }

  /**
   * @type {MenuItemProps[]}
   */
  const items = [
    {
      label: getValueLabel(campusItems, campus, 'Campus'),
      value: campus || 'Campus',
      subMenuType: 'campus',
      subMenu: FormInstance.fields.campus.options,
    },
    {
      label: getValueLabel(FormInstance.fields.session.options, academicPeriod, 'Session'),
      value: academicPeriod || 'Session',
      subMenuType: 'session',
      subMenu: FormInstance.fields.session.options.slice(0).reverse(),
    },
  ]  

  return <Menu items={items} />
}

/**
 * @typedef {object} MenuProps
 * @property {MenuItemProps[]=} items
 */

/**
 * @param {MenuProps} props
 */

const Menu = ({ items = [] }) => {
  const [activeIndex, setActiveIndex] = useState(-1)
  const [posLeft, setPosLeft] = useState(0)
  const tabRefs = useRef(/** @type {Array<HTMLButtonElement | null>} */ [])
  const contentRef = useRef(/** @type {HTMLDivElement | null} */ null)
  const { t } = useI18n()

  const setActive = (/** @type {number} */ i) => {
    const current = tabRefs.current[i]
    setPosLeft(current?.offsetLeft || 0)
    setActiveIndex((index) => (index === i ? -1 : i))
    contentRef.current.focus()
  }

  return (
    <div className="menu-campus-session">
      <div className="menu-campus-session__tabs">
        {items.map((item, i) => (
          <button
            key={i}
            type="button"
            ref={(el) => (tabRefs.current[i] = el)}
            className={cn('menu-campus-session__tab-btn', activeIndex === i && 'active')}
            onClick={() => setActive(i)}
          >
            <span className="menu-campus-session__tab-label">{item.label}</span>
            <span className="menu-campus-session__chevron"></span>
          </button>
        ))}
        <a
          className="menu-campus-session__accent-link"
          href="https://myworkday.ubc.ca"
        >
          {t('LOGIN TO WORKDAY')}
        </a>
      </div>
      <div
        ref={contentRef}
        className="menu-campus-session__tab-content"
        tabIndex={0}
        style={{
          // @ts-ignore
          '--menu-pos-left': `${posLeft}px`,
        }}
      >
        {items.map((item, i) => (
          <SubMenu
            key={i}
            subMenu={item.subMenu}
            subMenuType={item.subMenuType}
            isActive={activeIndex === i}
            onClose={() => setActiveIndex(-1)}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * @param {SubMenuProps} props
 */

const SubMenu = ({ subMenu = [], subMenuType = 'campus', isActive = false, onClose }) => {
  const dispatch = useDispatch()

  /**
   * @param {string} value
   */
  const handleSubMenuClick = (value) => {
    if (subMenuType === 'campus') dispatch(setCampus(value))
    else if (subMenuType === 'session') dispatch(setAcademicPeriod(value))
    onClose?.()
  }

  return (
    <div
      id={`menu-links-${subMenuType}`}
      className={cn('menu-campus-session__menu-links', isActive && 'active')}
    >
      {subMenu.map((item, i) => (
        <button
          className="menu-campus-session__menu-link"
          key={i}
          onClick={() => handleSubMenuClick(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )  
}